// Modules
import React, { createContext, useContext, useEffect, useState } from 'react'

// Application
import { Contact, PersonalDataMDM } from 'typings'
import { AccountState, ActivateStep } from 'typings/account'

const NAV_LS_KEY = '@NavAuth'

interface ContextData {
    // State
    state: {
        step: ActivateStep
        hasMounted: boolean
        isSubmitting: boolean
        id?: number
        name?: string
        socialName?: string
        cpf?: string
        birthDate?: string
        term?: boolean
        biologicalSex?: string
        password?: string
        postalCode?: string
        contact?: Contact
        email?: string
        phone?: string
        phoneContacts?: Contact[]
        emailContacts?: Contact[]
        isRecoverPassword?: boolean
        destinationToken?: string
        patientLoginMdmAccount?: string
        tokenCIP?: string
    }
    // Operations
    operations: {
        previousStep(): void
        nextStep(): void
        resetStep(): void
        setSubmitting(value: boolean): Promise<void>
        setPersonalData(personalData: PersonalDataMDM): Promise<void>
        setBiologicalSex(biologicalSex: string): Promise<void>
        setCpf(cpf: string): Promise<void>
        setPassword(password: string): Promise<void>
        setPostalCode(postalCode: string): Promise<void>
        setEmail(email: string): Promise<void>
        setPhone(cellphone: string): Promise<void>
        setContact(contact: Contact): Promise<void>
        setPhoneContacts(phoneContacts: Contact[]): Promise<void>
        setEmailContacts(emailContacts: Contact[]): Promise<void>
        resetAccountState(): Promise<void>
        setDestinationToken(destinationToken: string): Promise<void>
        setAccount(account: AccountState): Promise<void>
        setPatientLoginMdmAccount(patientCreateLoginMdmAccount: string): Promise<void>
        setTokenCIP(tokenCIP: string): Promise<void>
    }
}

const AccountContext = createContext<ContextData>({} as ContextData)

export const AccountProvider: React.FC = ({ children }) => {
    const [step, _setStep] = useState<ActivateStep>(0)
    const [hasMounted, _setMounted] = useState<boolean>(false)
    const [isSubmitting, _setSubmitting] = useState<boolean>(false)
    const [accountData, setAccountData] = useState<AccountState | null>(null)

    useEffect(() => {
        if (accountData) {
            _setMounted(true)
        }
    }, [accountData])

    const setAccount = async (account: AccountState): Promise<void> => {
        setAccountData(current => ({ ...current, ...account }))
    }

    const setPersonalData = async (personalData: PersonalDataMDM): Promise<void> => {
        setAccountData(current => ({ ...current, ...personalData }))
    }

    const setCpf = async (cpf: string): Promise<void> => {
        setAccountData(current => ({ ...current, cpf }))
    }

    const setBiologicalSex = async (biologicalSex: string) => {
        setAccountData(current => ({ ...current, biologicalSex }))
        sessionStorage.setItem('@LiviaAuth:SignUpBiologicalSex', JSON.stringify(biologicalSex))
    }

    const setPassword = async (password: string) => {
        setAccountData(current => ({ ...current, password }))
    }

    const setPostalCode = async (postalCode: string) => {
        setAccountData(current => ({ ...current, postalCode }))
        sessionStorage.setItem('@LiviaAuth:SignUpPostalCode', JSON.stringify(postalCode))
    }
    const setEmail = async (email: string): Promise<void> => {
        setAccountData(current => ({ ...current, email }))
    }

    const setPhone = async (phone: string): Promise<void> => {
        setAccountData(current => ({ ...current, phone }))
    }

    const setTokenCIP = async (tokenCIP: string): Promise<void> => {
        setAccountData(current => ({ ...current, tokenCIP }))
    }

    const setContact = async (contact: Contact): Promise<void> => {
        setAccountData(current => ({ ...current, contact }))
        sessionStorage.setItem(`${NAV_LS_KEY}:Contact`, JSON.stringify(contact))
    }

    const setPhoneContacts = async (phoneContacts: Contact[]): Promise<void> => {
        setAccountData(current => ({ ...current, phoneContacts }))
    }

    const setEmailContacts = async (emailContacts: Contact[]): Promise<void> => {
        setAccountData(current => ({ ...current, emailContacts }))
    }

    const setDestinationToken = async (destinationToken: string): Promise<void> => {
        setAccountData(current => ({ ...current, destinationToken }))
    }

    const setPatientLoginMdmAccount = async (patientLogin: string): Promise<void> => {
        setAccountData(current => ({ ...current, patientLogin }))
    }

    const setSubmitting = async (value: boolean): Promise<void> => {
        _setSubmitting(value)
    }

    const nextStep = (): void => {
        _setStep(value => value + 1)
    }

    const previousStep = (): void => {
        _setStep(value => value - 1)
    }

    const resetStep = (): void => {
        _setStep(0)
    }

    const resetAccountState = async (): Promise<void> => {
        setAccountData({
            id: undefined,
            name: undefined,
            socialName: undefined,
            cpf: undefined,
            birthDate: undefined,
            term: undefined,
            biologicalSex: undefined,
            password: undefined,
            postalCode: undefined,
            contact: undefined,
            email: undefined,
            phone: undefined,
            phoneContacts: undefined,
            emailContacts: undefined,
            destinationToken: undefined,
            tokenCIP: undefined,
        })
    }

    return (
        <AccountContext.Provider
            value={{
                state: {
                    step,
                    hasMounted,
                    isSubmitting,
                    ...accountData!,
                },
                operations: {
                    nextStep,
                    previousStep,
                    resetStep,
                    setPersonalData,
                    setCpf,
                    setPassword,
                    setPostalCode,
                    setEmail,
                    setPhone,
                    setContact,
                    setPhoneContacts,
                    setEmailContacts,
                    setSubmitting,
                    resetAccountState,
                    setDestinationToken,
                    setAccount,
                    setPatientLoginMdmAccount,
                    setBiologicalSex,
                    setTokenCIP,
                },
            }}
        >
            {children}
        </AccountContext.Provider>
    )
}

export function useAccountContext(): ContextData {
    const context = useContext(AccountContext)
    if (context === undefined) {
        throw new Error('useAccountContext value is undefined. Make sure you use the AccountProvider before using the context.')
    }
    return context
}
